import React from 'react';
import { Comment, Icon, Label } from 'semantic-ui-react';
import { formatDistanceToNow } from 'date-fns';
import './MessageBubble.css';

/**
 * Unescape HTML entities produced by validator.escape() on the backend.
 * Handles: &amp; &lt; &gt; &quot; &#x27; &#x2F;
 */
const unescapeHtml = (str) => {
  if (!str) return str;
  const doc = new DOMParser().parseFromString(str, 'text/html');
  return doc.documentElement.textContent;
};

/**
 * Generate a colored circle avatar with the user's initial
 */
const InitialAvatar = ({ name, size = 32 }) => {
  const initial = (name || '?').charAt(0).toUpperCase();
  // Generate a consistent color from the name
  const colors = ['#2185d0', '#21ba45', '#f2711c', '#a333c8', '#e03997', '#00b5ad', '#6435c9', '#b5cc18'];
  const colorIndex = (name || '').split('').reduce((acc, c) => acc + c.charCodeAt(0), 0) % colors.length;
  const bgColor = colors[colorIndex];

  return (
    <div style={{
      width: size,
      height: size,
      borderRadius: '50%',
      backgroundColor: bgColor,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: '#fff',
      fontWeight: 'bold',
      fontSize: size * 0.45,
      flexShrink: 0
    }}>
      {initial}
    </div>
  );
};

const MessageBubble = ({ message, isCurrentUser, showAvatar, participant }) => {
  const formatTime = (timestamp) => {
    try {
      const date = new Date(timestamp);
      return formatDistanceToNow(date, { addSuffix: true });
    } catch (error) {
      return 'Recently';
    }
  };

  const getMessageTypeLabel = (messageType) => {
    switch (messageType) {
      case 'OPPORTUNITY_INQUIRY':
        return { text: 'Opportunity Inquiry', color: 'blue' };
      case 'APPLICATION_RESPONSE':
        return { text: 'Application Response', color: 'green' };
      case 'SYSTEM':
        return { text: 'System Message', color: 'grey' };
      default:
        return null;
    }
  };

  const messageTypeLabel = getMessageTypeLabel(message.messageType);

  return (
    <Comment className={`message-bubble ${isCurrentUser ? 'current-user' : 'other-user'}`}>
      {showAvatar && !isCurrentUser && (
        <div className="comment-avatar-wrapper" style={{ marginRight: '8px', flexShrink: 0 }}>
          {participant?.avatar ? (
            <Comment.Avatar
              src={participant.avatar}
              alt={participant?.name || 'User'}
            />
          ) : (
            <InitialAvatar name={participant?.name || participant?.email || 'User'} size={35} />
          )}
        </div>
      )}

      <Comment.Content className="message-content">
        {showAvatar && (
          <Comment.Author as="span" className="message-author">
            {isCurrentUser ? 'You' : (participant?.name || 'User')}
          </Comment.Author>
        )}

        <Comment.Metadata className="message-metadata">
          <span className="message-time">{formatTime(message.createdAt)}</span>
          {message.isRead && isCurrentUser && (
            <Icon name="check" className="read-indicator" />
          )}
        </Comment.Metadata>

        {messageTypeLabel && (
          <div className="message-type-label">
            <Label size="mini" color={messageTypeLabel.color}>
              {messageTypeLabel.text}
            </Label>
          </div>
        )}

        <Comment.Text className="message-text">
          {unescapeHtml(message.content)}
        </Comment.Text>

        {message.replyTo && (
          <div className="reply-context">
            <Icon name="reply" size="small" />
            <span className="reply-preview">
              Replying to: {message.replyTo.content}
            </span>
          </div>
        )}

        {message.relatedEntity && (
          <div className="related-entity">
            <Icon name="link" size="small" />
            <span className="entity-info">
              Related to {message.relatedEntity.type}: {message.relatedEntity.title || message.relatedEntity.uuid}
            </span>
          </div>
        )}
      </Comment.Content>
    </Comment>
  );
};

export default MessageBubble;
